/**
 * Template Selector Component
 * Reusable component for selecting and loading communication templates
 */

import React, { useState, useEffect } from 'react';
import { FileText, ChevronDown, Upload, Info } from 'lucide-react';
import { config } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface Template {
  id: number;
  name: string;
  description: string | null;
  template_type: string;
  communication_method: string;
  subject: string | null;
  body: string;
  variable_fields: string[];
  is_active: boolean;
}

interface TemplateSelectorProps {
  /** Communication method to filter templates (email, sms, whatsapp, voice) */
  communicationMethod: string;
  /** Callback when template is loaded */
  onTemplateSelect: (template: Template) => void;
  /** Optional className for styling */
  className?: string;
  /** Show detailed variable preview */
  showVariablePreview?: boolean;
  /** Optional context data for previewing variable replacements */
  previewContext?: Record<string, any>;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  communicationMethod,
  onTemplateSelect,
  className = '',
  showVariablePreview = true,
  previewContext = {}
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [communicationMethod]);

  const syncVoiceTemplates = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/voice-workflows/sync-to-templates`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to sync templates: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Voice templates synced:', result);

      // Refresh templates after sync
      await fetchTemplates();
    } catch (err) {
      console.error('Error syncing voice templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync voice templates');
    } finally {
      setSyncing(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const params = new URLSearchParams({
        communication_method: communicationMethod,
        is_active: 'true'
      });

      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/communication-templates?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
      }

      const data = await response.json();
      setTemplates(data || []);

      // Auto-sync voice templates if none exist for voice communication
      if ((communicationMethod === 'voice' || communicationMethod === 'phone') && data.length === 0 && !syncing) {
        console.log('No voice templates found, auto-syncing...');
        await syncVoiceTemplates();
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = parseInt(e.target.value);
    const template = templates.find(tmpl => tmpl.id === templateId);
    setSelectedTemplate(template || null);
  };

  const handleLoadTemplate = () => {
    if (selectedTemplate) {
      onTemplateSelect(selectedTemplate);
    }
  };

  const getVariablePreview = (variableName: string): string => {
    // Try to get value from preview context
    if (previewContext[variableName]) {
      return String(previewContext[variableName]);
    }

    // Provide sample values for common variables
    const sampleValues: Record<string, string> = {
      member_first_name: 'John',
      member_last_name: 'Smith',
      member_full_name: 'John Smith',
      member_email: 'john.smith@example.com',
      member_phone: '(555) 123-4567',
      program_name: 'Wellness Program',
      case_manager_name: 'Jane Doe',
      case_manager_email: 'jane.doe@example.com',
      case_manager_phone: '(555) 987-6543',
      organization_name: 'Your Organization',
      appointment_date: '03/15/2025',
      appointment_time: '10:00 AM',
      current_date: fmt.short(new Date()),
      enrollment_date: '01/15/2025',
      next_contact_date: '03/01/2025'
    };

    return sampleValues[variableName] || `{{${variableName}}}`;
  };

  const getPreviewBody = (body: string): string => {
    if (!showVariablePreview || !selectedTemplate) return body;

    let preview = body;
    selectedTemplate.variable_fields.forEach(field => {
      const regex = new RegExp(`{{${field}}}`, 'g');
      preview = preview.replace(regex, getVariablePreview(field));
    });
    return preview;
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Template Selector */}
      <div className="bg-paper-2 border border-v2border rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-ink-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-ink mb-1">
                {t('communications.use_message_template', 'Use Message Template')}
              </h3>
              <p className="text-xs text-ink-500">
                {t('communications.select_a_prebuilt_template_to', 'Select a pre-built template to quickly compose your message. Templates support dynamic variables that will be replaced with actual member data.')}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 dark:border-red-700 rounded-md p-2 text-xs text-red-700">
                {error}
              </div>
            )}

            {loading || syncing ? (
              <div className="text-sm text-ink-500">
                {syncing ? 'Syncing voice templates...' : 'Loading templates...'}
              </div>
            ) : templates.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-sm text-amber-800 mb-2">
                  No templates available for {communicationMethod}.
                </p>
                {(communicationMethod === 'voice' || communicationMethod === 'phone') ? (
                  <button
                    onClick={syncVoiceTemplates}
                    className="text-sm font-semibold text-amber-900 hover:text-amber-950 underline"
                  >
                    {t('communications.click_here_to_sync_voice', 'Click here to sync Voice AI templates')}
                  </button>
                ) : (
                  <p className="text-xs text-amber-700">
                    {t('communications.create_templates_using', 'Create templates using')} <span className="font-semibold">{t('communications.communication_templates', 'Communication Templates')}</span> in the Administration menu
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <select
                      value={selectedTemplate?.id || ''}
                      onChange={handleTemplateChange}
                      className="w-full border border-v2border rounded-lg px-3 py-2 pr-8 text-sm text-ink bg-surface focus:ring-2 focus:ring-v2accent focus:border-v2accent appearance-none"
                    >
                      <option value="">-- Select a template --</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} {template.description ? `- ${template.description}` : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>

                  <button
                    onClick={handleLoadTemplate}
                    disabled={!selectedTemplate}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedTemplate
                        ? 'bg-v2accent text-white hover:bg-v2accent/90 active:scale-95'
                        : 'bg-paper-2 text-ink-400 cursor-not-allowed'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    {t('communications.load', 'Load')}
                  </button>
                </div>

                {/* Template Preview */}
                {selectedTemplate && showVariablePreview && (
                  <div className="space-y-2">
                    <div className="bg-surface border border-v2border rounded-md p-3">
                      <div className="text-xs font-medium text-ink-700 mb-1">{t('communications.template_preview', 'Template Preview:')}</div>

                      {selectedTemplate.subject && (
                        <div className="mb-2 pb-2 border-b border-gray-200">
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('communications.subject', 'Subject:')}</div>
                          <div className="text-sm text-gray-900 font-medium">
                            {getPreviewBody(selectedTemplate.subject)}
                          </div>
                        </div>
                      )}

                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('communications.message', 'Message:')}</div>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                          {getPreviewBody(selectedTemplate.body)}
                        </div>
                      </div>
                    </div>

                    {/* Variable Fields Info */}
                    {selectedTemplate.variable_fields.length > 0 && (
                      <div className="bg-surface border border-v2border rounded-md p-3">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-ink-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="text-xs font-medium text-gray-700 mb-1">
                              Dynamic Variables ({selectedTemplate.variable_fields.length}):
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {selectedTemplate.variable_fields.map(field => (
                                <span
                                  key={field}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-paper-2 text-ink-700 border border-v2border"
                                >
                                  {`{{${field}}}`}
                                </span>
                              ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              {t('communications.these_placeholders_will_be_replaced', 'These placeholders will be replaced with actual member data when the message is sent.')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
